import React, { useCallback, useState } from "react";
import axiosInstance from "../utils/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "../components/Navbar";

const AboutUs = () => {
  const [isSending, setIsSending] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [showModal, setShowModal] = useState(false);

  const handleRequestDemo = useCallback(async () => {
    try {
      // Basic validation
      const emailTrim = email.trim();
      if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
        toast.warning("Please enter a valid email address");
        return;
      }

      setIsSending(true);

      const page = typeof window !== "undefined" ? window.location.href : "";
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";

      await axiosInstance.post("/email/request-demo", {
        name: name.trim() || undefined,
        email: emailTrim,
        notes: undefined,
        pageUrl: page,
        userAgent: ua,
      });

      toast.success("Thanks! Our team will reach out to schedule your demo.");
      setEmail("");
      setName("");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to send request. Please try again later.";
      toast.error(msg);
    } finally {
      setIsSending(false);
    }
  }, [email, name]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <ToastContainer position="top-center" autoClose={2500} />
      <main>
        {/* Welcome Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-8">Welcome!</h1>
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  We are team of dedicated entrepreneurs, prominent leaders in the AI domain including AI Faculty at California State University with a collective 30+ years of Health + technology experience bringing Innovations to improve the Surgical Standard of Care for patient engagements via compliance and nursing efficiencies!
                </p>
                <p>
                  Our AI solutions cover the entire surgical process where patients are engage, i.e., Pre & Post Surgery Compliance, Perio-Anesthesia, Wound Care and Pain Management including Medication Adherence.
                </p>
                <p>
                  Please contact us for more information via{" "}
                  <a href="mailto:healthsnai@gmail.com" className="text-yellow-500 hover:text-yellow-600 underline">
                    healthsnai@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* AI Agents Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Heading */}
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl font-extrabold tracking-tight text-gray-900">
                No-Code, Low-Code & CRUD AI Agents
              </h1>
              <p className="mt-4 text-gray-600">
                Build and deploy healthcare-ready AI agents faster—drag-and-drop simplicity, developer control when you need it, and secure CRUD operations out of the box.
              </p>
            </div>

            {/* Feature Cards */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* No-Code */}
              <div className="group relative rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute inset-x-0 -top-0.5 h-0.5 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 rounded-t-2xl" />
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-yellow-100 text-yellow-700 flex items-center justify-center">
                    {/* Puzzle icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6"><path d="M11 2a2 2 0 0 1 2 2v1h1a2 2 0 1 1 0 4h-1v2h1a2 2 0 1 1 0 4h-1v1a2 2 0 1 1-4 0v-1H8a2 2 0 1 1 0-4h1V9H8a2 2 0 1 1 0-4h1V4a2 2 0 0 1 2-2Z"/></svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">No‑Code Builder</h3>
                </div>
                <p className="mt-3 text-sm text-gray-600">Launch useful agents with zero code—intake, reminders, triage, and more.</p>
                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  <li>✅ Drag‑and‑drop flows</li>
                  <li>✅ HIPAA‑aligned templates</li>
                  <li>✅ Smart variables & routing</li>
                </ul>
              </div>

              {/* Low-Code */}
              <div className="group relative rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute inset-x-0 -top-0.5 h-0.5 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 rounded-t-2xl" />
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-yellow-100 text-yellow-700 flex items-center justify-center">
                    {/* Brackets icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6"><path d="M8 4v3H6v10h2v3H3V4h5Zm8 0h5v16h-5v-3h2V7h-2V4Z"/></svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Low‑Code Workflows</h3>
                </div>
                <p className="mt-3 text-sm text-gray-600">Extend, integrate, and fine‑tune with serverless actions and SDK hooks.</p>
                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  <li>✅ Custom actions & webhooks</li>
                  <li>✅ EHR & calendar integrations</li>
                  <li>✅ Role‑based access control</li>
                </ul>
              </div>

              {/* CRUD Agents */}
              <div className="group relative rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="absolute inset-x-0 -top-0.5 h-0.5 bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500 rounded-t-2xl" />
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-yellow-100 text-yellow-700 flex items-center justify-center">
                    {/* Database icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6"><path d="M12 2C7.58 2 4 3.79 4 6v12c0 2.21 3.58 4 8 4s8-1.79 8-4V6c0-2.21-3.58-4-8-4Zm0 2c3.87 0 6 .99 6 2s-2.13 2-6 2-6-.99-6-2 2.13-2 6-2Zm6 5.09V11c0 1.01-2.13 2-6 2s-6-.99-6-2V9.09C7.29 9.67 9.4 10 12 10s4.71-.33 6-0.91Zm0 5V17c0 1.01-2.13 2-6 2s-6-.99-6-2v-1.91C7.29 15.67 9.4 16 12 16s4.71-.33 6-0.91Z"/></svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">CRUD‑Native Agents</h3>
                </div>
                <p className="mt-3 text-sm text-gray-600">Secure data operations with audit trails and least‑privilege by default.</p>
                <ul className="mt-4 space-y-2 text-sm text-gray-700">
                  <li>✅ Structured prompts & policies</li>
                  <li>✅ Field‑level permissions</li>
                  <li>✅ Observability & logs</li>
                </ul>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 text-center">
              <button
                className="inline-flex items-center justify-center bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2.5 px-6 rounded-xl shadow-lg hover:shadow-xl transition"
                onClick={() => setShowModal(true)}
              >
                REQUEST DEMO
              </button>
            </div>

            {/* Diagram */}
            <div className="mt-14 max-w-4xl mx-auto">
              <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-md bg-white">
                <img
                  src="/imgs/arc-diagram.png"
                  alt="AI Agents architecture overview"
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Partner logos */}
            <div className="mt-14">
              <p className="text-center text-sm uppercase tracking-wider text-gray-500">Trusted by innovators</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
                <a href="https://www.allyhealthsolutions.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center rounded-xl bg-white border border-gray-200 p-6 shadow-sm hover:shadow-md transition">
                  <img src="./imgs/aff-3.png" alt="Ally Health Solutions" className="h-14 w-auto object-contain" />
                </a>
                <a href="https://www.toptierimpact.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center rounded-xl bg-white border border-gray-200 p-6 shadow-sm hover:shadow-md transition">
                  <img src="./imgs/aff-4.png" alt="Top Tier Impact" className="h-14 w-auto object-contain" />
                </a>
              </div>
            </div>

          </div>
        </section>

        {/* Contact Footer Section */}
        <section 
          className="py-16 text-white relative"
          style={{
            backgroundImage: "url('/imgs/sm-bg.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* Background overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/80 via-yellow-600/70 to-yellow-600/90"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {/* Contact Info */}
              <div className="space-y-6">
                <h2 className="text-4xl font-bold drop-shadow-lg">JOIN the smartNURSE Revolution...</h2>
                <h2 className="text-2xl text-black font-bold">
                  <a href="https://www.smartnurse.ai/" className="hover:text-yellow-200 drop-shadow-lg transition-colors">
                    www.smartNURSE.ai
                  </a>
                </h2>
                <div className="flex items-center space-x-3">
                  <i className="fas fa-mail-bulk text-xl drop-shadow-lg"></i>
                  <p className="text-lg">
                    <a href="mailto:healthsnai@gmail.com" className="hover:text-yellow-200 transition-colors">
                      healthsnai@gmail.com
                    </a>
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="fas fa-phone-alt text-xl drop-shadow-lg"></i>
                  <p className="text-lg">
                    <a href="tel:+19257442606" className="hover:text-yellow-200 transition-colors">
                      +(1) 925 744 2606
                    </a>
                  </p>
                </div>
              </div>

              {/* Navigation Links */}
              <div className="space-y-6 flex flex-col items-center">
                <h2 className="text-2xl font-bold">
                  <a href="https://www.smartnurse.ai/" className="hover:text-yellow-200 drop-shadow-lg transition-colors">
                    Home
                  </a>
                </h2>
                <h2 className="text-2xl font-bold">
                  <a href="https://www.smartnurse.ai/about-us/" className="hover:text-yellow-200 drop-shadow-lg transition-colors">
                    About Us
                  </a>
                </h2>
                <div className="mt-8 w-full max-w-md">
                  <img 
                    src="/imgs/smartnurse-logo.webp" 
                    alt="SmartNurse Logo" 
                    className="w-48 h-auto drop-shadow-xl"
                  />
                </div>
                <button
                  className="mt-4 bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-6 rounded-xl transition-colors shadow-lg hover:shadow-2xl"
                  onClick={() => setShowModal(true)}
                >
                  REQUEST DEMO
                </button>
              </div>

              {/* Logo/Image */}
              <div className="flex justify-center">
                <img 
                  src="/imgs/all-in-one.png" 
                  alt="SmartNurse Character" 
                  className="w-48 h-auto drop-shadow-2xl"
                />
              </div>
            </div>
          </div>

        </section>

          {/* Add here the footer note */}
          <footer className="bg-yellow-500 border-t border-yellow-600 py-2">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="text-center">
                <h2 className="text-lg md:text-sm text-black leading-relaxed space-x-2 font-light">
                  <a href="https://www.smartnurse.ai/faq/" className="hover:text-yellow-300 transition-colors">
                    FAQS
                  </a>
                  <span className="mx-2">|</span>
                  <a href="https://www.smartnurse.ai/privacy-policy/" className="hover:text-yellow-300 transition-colors">
                    Privacy Policy
                  </a>
                  <span className="mx-2">|</span>
                  <a href="https://www.smartnurse.ai/terms-conditions/" className="hover:text-yellow-300 transition-colors">
                    Terms & Conditions
                  </a>
                  <span className="mx-2">|</span>
                  <a href="https://www.smartnurse.ai/signup/" className="hover:text-yellow-300 transition-colors">
                    Contact Us
                  </a> 
                  <span className="text-black mt-2 pl-8 inline-block">
                    © 2025 Livinbetr Corp ( SmartNurse.ai)- All Rights Reserved
                  </span>
                </h2>
              </div>
            </div>
          </footer>
      </main>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => (!isSending ? setShowModal(false) : null)}
          />
          {/* Modal */}
          <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl p-6">
            <div className="flex items-start justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Request a demo</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => (!isSending ? setShowModal(false) : null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">Enter your details and we will contact you shortly.</p>

            <div className="mt-4 space-y-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your Name (optional)"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                disabled={isSending}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your Email (required)"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
                disabled={isSending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSending) {
                    e.preventDefault();
                    handleRequestDemo();
                  }
                }}
              />
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => (!isSending ? setShowModal(false) : null)}
                disabled={isSending}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-lg font-semibold text-white ${
                  isSending ? 'bg-yellow-400 cursor-not-allowed' : 'bg-yellow-500 hover:bg-yellow-600'
                }`}
                onClick={isSending ? undefined : async () => {
                  await handleRequestDemo();
                  if (!isSending) {
                    setShowModal(false);
                  }
                }}
                aria-disabled={isSending}
              >
                {isSending ? 'SENDING…' : 'Request Demo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;
